import { makeObservable, observable, action, runInAction } from "mobx";
import { Duration } from "luxon";

import { uuidModule } from "@/modules/uuid";
import { RecordingStatus } from "@/store/audio-capture/models/recording-status";
import type {
  RecordingEngineType,
  RecordingSessionUpdateArgs,
  PersistedAudioCaptureState,
} from "@/store/audio-capture/persistence/audio-capture-persistence";
import { triggerAutoStop } from "@/store/audio-capture/utils/session";
import {
  formatDurationLimitLabel,
  formatElapsedDuration,
} from "@/store/audio-capture/utils/duration-formatting";
import type { AppStoreAudioCaptureStore } from "@/store/audio-capture/AppStoreAudioCaptureStore";
import type { Uuid } from "@/domains/global/identifiers";
import type { AutoStopReason } from "@/store/audio-capture/AppStoreAudioCaptureStore";

type BeginRecordingSessionArgs = {
  noteId: string;
  engine: RecordingEngineType;
  isRecordingWithoutTranscription: boolean;
  clearExisting: boolean;
};

type SetStatusArgs = {
  status: RecordingStatus;
};

type SetPausedTimeArgs = {
  timestamp: number;
};

type IncrementTotalPausedTimeArgs = {
  delta: number;
};

type SetAutoStopModalVisibleArgs = {
  open: boolean;
};

type HasRecordingForNoteArgs = {
  noteId: Uuid;
};

type SetStartTimeArgs = {
  timestamp: number;
};

export class SessionManager {
  duration = 0;
  startTime = 0;
  pausedTime = 0;
  totalPausedTime = 0;
  durationInterval: NodeJS.Timeout | null = null;
  autoStopModalVisible = false;
  autoStopReason: AutoStopReason | null = null;
  autoStopDurationSeconds: number | null = null;

  constructor(private readonly store: AppStoreAudioCaptureStore) {
    makeObservable(this, {
      hasRecordingForNote: true,
      getAutoStopDialogContent: true,
      duration: observable,
      startTime: observable,
      pausedTime: observable,
      totalPausedTime: observable,
      durationInterval: observable,
      autoStopModalVisible: observable,
      autoStopReason: observable,
      autoStopDurationSeconds: observable,
      beginRecordingSession: action,
      restoreRecordingSessionFromState: action,
      completeCurrentSession: action,
      updateSession: action,
      setAudioSilenceSince: action,
      updateUploadProgress: action,
      updateDuration: action,
      setStatus: action,
      setPausedTime: action,
      incrementTotalPausedTime: action,
      clearDurationInterval: action,
      startDurationInterval: action,
      resetAutoStopState: action,
      setAutoStopModalVisible: action,
      updateAutoStopTriggered: action,
      setStartTime: action,
      resetStartTime: action,
      resetPausedTime: action,
      resetTotalPausedTime: action,
      resetDuration: action,
      resetTimingState: action,
    });
  }

  beginRecordingSession({
    noteId,
    engine,
    isRecordingWithoutTranscription,
    clearExisting,
  }: BeginRecordingSessionArgs) {
    const existingSession =
      !clearExisting && this.store.currentSession?.noteId === noteId
        ? this.store.currentSession
        : null;

    if (!existingSession && clearExisting) {
      this.store.audioChunks = [];
      this.store.capturedAudioId = uuidModule.generate();
    }

    const recordingId = existingSession ? existingSession.recordingId : uuidModule.generate();
    const startedAt = Date.now();

    this.store.currentSession = {
      noteId,
      recordingId,
      capturedAudioId: this.store.capturedAudioId,
      engine,
      startedAt,
      isRecordingWithoutTranscription,
    };

    if (clearExisting || !existingSession) {
      void this.store.persistence.startSession({
        noteId,
        recordingId,
        capturedAudioId: this.store.capturedAudioId,
        engine,
        startedAt,
        isRecordingWithoutTranscription,
        clearExisting,
      });
    }

    this.resetTimingState();

    this.updateSession({
      status: RecordingStatus.INITIALIZING,
      totalCapturedMs: 0,
      pausedAccumulatedMs: 0,
      silenceSince: null,
      autoStopReason: null,
      uploadProgressPercent: null,
    });
  }

  restoreRecordingSessionFromState(state: PersistedAudioCaptureState) {
    const recordingId = state.recordingId ?? state.capturedAudioId ?? uuidModule.generate();
    const engine = state.engine ?? "unknown";

    this.store.capturedAudioId = state.capturedAudioId;
    this.store.currentSession = {
      noteId: state.noteId,
      recordingId,
      capturedAudioId: state.capturedAudioId,
      engine,
      startedAt: Date.now(),
      isRecordingWithoutTranscription: false,
    };
  }

  completeCurrentSession() {
    if (!this.store.currentSession) return;
    const session = this.store.currentSession;
    void this.store.persistence.completeSession({
      noteId: session.noteId,
      recordingId: session.recordingId,
      completedAt: Date.now(),
    });
    this.store.currentSession = null;
  }

  updateSession(update: Partial<Omit<RecordingSessionUpdateArgs, "noteId" | "recordingId">>) {
    if (!this.store.currentSession) return;
    void this.store.persistence.updateSession({
      noteId: this.store.currentSession.noteId,
      recordingId: this.store.currentSession.recordingId,
      ...update,
    });
  }

  setAudioSilenceSince(value: number | null) {
    runInAction(() => {
      this.store.audioIsSilentSince = value;
    });
    this.updateSession({ silenceSince: value });
  }

  updateUploadProgress(percent: number | null) {
    runInAction(() => {
      this.store.uploadProgressPercent = percent;
    });
    this.updateSession({ uploadProgressPercent: percent });
  }

  updateDuration() {
    const currentTime = Date.now();
    const elapsedTime = currentTime - this.startTime - this.totalPausedTime;
    const elapsedSeconds = Math.max(0, Math.floor(elapsedTime / 1000));

    this.duration = elapsedSeconds;
    this.updateSession({
      totalCapturedMs: elapsedSeconds * 1000,
      pausedAccumulatedMs: this.totalPausedTime,
    });

    this.store.updateFriendlyDebugInfo();

    const elapsedDuration = Duration.fromObject({ seconds: elapsedSeconds });
    const maxDurationMilliseconds = this.store.MAX_RECORDING_DURATION.as("milliseconds");
    if (
      this.store.status === RecordingStatus.RECORDING &&
      !this.autoStopReason &&
      elapsedDuration.as("milliseconds") >= maxDurationMilliseconds
    ) {
      triggerAutoStop(this.store, "duration-limit");
    }
  }

  async hasRecordingForNote({ noteId }: HasRecordingForNoteArgs) {
    if (this.store.activeNoteId === noteId && this.store.status !== RecordingStatus.IDLE) {
      return true;
    }

    return this.store.persistence.hasAnyForScope({ noteId });
  }

  setStatus({ status }: SetStatusArgs) {
    this.updateSession({ status });
    runInAction(() => {
      this.store.status = status;
    });
    this.store.updateFriendlyDebugInfo();
  }

  setStartTime({ timestamp }: SetStartTimeArgs) {
    this.startTime = timestamp;
  }

  resetStartTime() {
    this.startTime = 0;
  }

  setPausedTime({ timestamp }: SetPausedTimeArgs) {
    this.pausedTime = timestamp;
  }

  resetPausedTime() {
    this.pausedTime = 0;
  }

  incrementTotalPausedTime({ delta }: IncrementTotalPausedTimeArgs) {
    this.totalPausedTime += delta;
  }

  resetTotalPausedTime() {
    this.totalPausedTime = 0;
  }

  resetDuration() {
    this.duration = 0;
  }

  resetTimingState() {
    this.resetDuration();
    this.resetPausedTime();
    this.resetTotalPausedTime();
    this.clearDurationInterval();
  }

  clearDurationInterval() {
    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }
  }

  startDurationInterval() {
    this.clearDurationInterval();
    this.durationInterval = setInterval(() => {
      this.updateDuration();
    }, this.store.DURATION_UPDATE_INTERVAL_MS);
  }

  resetAutoStopState() {
    this.autoStopModalVisible = false;
    this.autoStopReason = null;
    this.autoStopDurationSeconds = null;
  }

  setAutoStopModalVisible({ open }: SetAutoStopModalVisibleArgs) {
    this.autoStopModalVisible = open;
    if (!open) {
      this.autoStopReason = null;
      this.autoStopDurationSeconds = null;
    }
    this.store.updateFriendlyDebugInfo();
    if (!open) {
      this.updateSession({ autoStopReason: null });
    }
  }

  getAutoStopDialogContent(): {
    title: string;
    message: string;
    limitLabel: string;
    currentDurationLabel: string | null;
  } | null {
    if (!this.autoStopReason) return null;

    const limitLabel = formatDurationLimitLabel(this.store.MAX_RECORDING_DURATION);
    const currentDurationSeconds =
      this.autoStopDurationSeconds ?? this.duration + this.store.restoredDuration;
    const currentDurationLabel = formatElapsedDuration(currentDurationSeconds);

    const title =
      this.autoStopReason === "duration-limit"
        ? "Recording stopped automatically"
        : "Recording stopped";
    const message =
      this.autoStopReason === "duration-limit"
        ? `Recording automatically stopped after reaching ${limitLabel}${currentDurationLabel ? ` (${currentDurationLabel})` : ""}.`
        : "Recording was stopped automatically.";

    return { title, message, limitLabel, currentDurationLabel };
  }

  updateAutoStopTriggered({ reason }: { reason: AutoStopReason }) {
    this.autoStopDurationSeconds = this.duration + this.store.restoredDuration;
    this.autoStopReason = reason;
    this.autoStopModalVisible = true;
    this.store.updateFriendlyDebugInfo({
      autoStopTriggeredAt: new Date().toISOString(),
    });
    this.updateSession({
      autoStopReason: reason,
      totalCapturedMs: (this.duration + this.store.restoredDuration) * 1000,
    });
  }
}
